import React, { useState } from 'react';
import {
    Box,
    Flex,
    Heading,
    Text,
    VStack,
    Image,
    ModalHeader,
    Button,
    Icon,
} from '@chakra-ui/react';
import { WarningTwoIcon, ArrowBackIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import BrandButton from '../brandButton/BrandButton';
import logo from '../../public/icons/logo_.svg';
import rocket from '../../public/icons/rocket.svg';

const founderStarterUniqueFeatures = [
    { name: 'Account & Profile', value: 'Multiple Active Profiles' },
    { name: 'View Founders & Investors', value: 'Full Access' },
    { name: 'Direct Messaging (Investors)', value: 'Full Messaging' },
    { name: 'Create Term Sheets', value: 'Yes' },
    { name: 'Search & View Investors (Full Directory)', value: 'Yes' },
    { name: 'Priority Listing', value: 'Basic Priority' },
];

const founderProUniqueFeatures = [
    { name: 'Request Pitch at Events', value: 'Yes' },
    { name: 'Priority Listing', value: 'Enhanced Priority' },
];


const founderFreeFeatures = [
    { name: 'Account & Profile', value: '1 Basic Profile' },
    { name: 'View Founders & Investors', value: 'Limited view' },
    { name: 'Community Updates', value: 'Yes' },
    { name: 'Direct Messaging (Investors)', value: 'Send-only (cannot read replies)' }
];

const investorFreeFeatures = [
    { name: 'View Founders', value: 'Limited preview (first 10 founders)' },
    { name: 'Founders Profile page View', value: 'Public info only - Basic information (startup name, industry, short description, logo)' },
    { name: 'Receive & Reply to Messages', value: 'Allowed' },
    { name: 'Participate in Pitch Events', value: 'Can view events' },
];

const investorStarterUniqueFeatures = [
    { name: 'Founder Search', value: 'Full search access' },
    { name: 'Initiate Messaging to Founders', value: 'Allowed - Can proactively contact founders' },
    { name: 'Access Pitch Decks / Documents', value: 'Allowed - Can view and download pitch decks' },
    { name: 'Verification Badge', value: 'Yes - Standard verification badge (Green)' },
    { name: 'Advanced Search Filters', value: 'Standard filters - Industry, stage and location' },
];

const investorProUniqueFeatures = [
    { name: 'Submit Termsheet', value: 'Submit termsheets for open deals' },
    { name: 'Request Pitch at Events', value: 'Yes' },
    { name: 'Priority Listing', value: 'Enhanced Priority' },
];


export const Plans = ({
    subscriptionPlans,
    loggedInUserSubscription,
    isDesktop,
    isCancelling,
    isResuming,
    onUpgrade,
    onCancel,
    onResume,
    userType = 'founder',
}) => {
    const plansArray = subscriptionPlans?.data || subscriptionPlans || [];
    const currentPlanName = loggedInUserSubscription?.subscription_plan?.name;
    const isCurrentPlanActive = loggedInUserSubscription?.status === 'active';
    const currentPlanPrice = parseFloat(loggedInUserSubscription?.subscription_plan?.price || 0);
    const isCurrentPlanFree = currentPlanPrice === 0;
    const isInvestor = userType?.toLowerCase() === 'investor';


    // Get plan tier: 0 = Free, 1 = Starter, 2 = Pro
    const getPlanTier = (planName) => {
        if (!planName) return 0;
        const planNameLower = planName.toLowerCase();
        if (planNameLower.includes('pro')) return 2;
        if (planNameLower.includes('starter')) return 1;
        return 0; // Free
    };

    const currentPlanTier = getPlanTier(currentPlanName);

    const getPlanFeaturesForCard = (planName) => {
        const planNameLower = planName?.toLowerCase() || '';

        if (isInvestor) {
            // Investor plans
            if (planNameLower.includes('starter')) {
                return {
                    isPro: false,
                    isStarter: true,
                    features: investorStarterUniqueFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            } else if (planNameLower.includes('pro')) {
                return {
                    isPro: true,
                    isStarter: false,
                    features: investorProUniqueFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            } else {
                return {
                    isPro: false,
                    isStarter: false,
                    features: investorFreeFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            }
        } else {
            // Founder plans (default)
            if (planNameLower.includes('starter')) {
                return {
                    isPro: false,
                    isStarter: true,
                    features: founderStarterUniqueFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            } else if (planNameLower.includes('pro')) {
                return {
                    isPro: true,
                    isStarter: false,
                    features: founderProUniqueFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            } else {
                return {
                    isPro: false,
                    isStarter: false,
                    features: founderFreeFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            }
        }
    };


    return (
        <>
            <ModalHeader px={{ base: 0, md: 4 }} pt={{ base: 2, md: 4 }} pb={{ base: 2, md: 4 }}>
                <Text
                    w="100%"
                    textAlign="center"
                    fontSize={{ base: '16px', md: '18px' }}
                    fontWeight={600}
                    color="brand.primary"
                >
                    Choose your preferred plan
                </Text>
            </ModalHeader>
            <Box
                display="flex"
                flexDir={isDesktop ? 'row' : 'column'}
                justifyContent="center"
                alignItems="center"
                gap={{ base: 3, md: 4 }}
                py={{ base: 4, md: 6 }}
                px={{ base: 2, md: 4 }}
            >
                {plansArray?.length > 0 ? (
                    plansArray.map((plan) => {
                        const isCurrentPlan = currentPlanName === plan?.name && isCurrentPlanActive;
                        const price = parseFloat(plan?.price || 0);
                        const isFreePlan = price === 0;
                        const planTier = getPlanTier(plan?.name);
                        const isLowerTierPlan = isCurrentPlanActive && planTier < currentPlanTier;
                        const isNotSelectable = (isFreePlan && !isCurrentPlan) || isLowerTierPlan;

                        return (
                            <Box
                                key={plan?.id || plan?.name}
                                p={{ base: 4, md: 6 }}
                                borderRadius="15px"
                                border="2px"
                                borderColor={
                                    isCurrentPlan
                                        ? 'brand.primary'
                                        : isNotSelectable
                                            ? 'gray.300'
                                            : 'gray.200'
                                }
                                bg={
                                    isCurrentPlan
                                        ? 'rgba(19, 74, 112, 0.05)'
                                        : isNotSelectable
                                            ? 'rgba(0, 0, 0, 0.02)'
                                            : 'white'
                                }
                                boxShadow={isCurrentPlan ? '0 4px 12px rgba(19, 74, 112, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)'}
                                position="relative"
                                minW={isDesktop ? '280px' : '100%'}
                                maxW={isDesktop ? '320px' : '100%'}
                                w="100%"
                                transition="all 0.3s ease"
                                opacity={isNotSelectable ? 0.5 : 1}
                                cursor={isNotSelectable ? 'not-allowed' : 'default'}
                                _hover={
                                    isNotSelectable || !isDesktop
                                        ? {}
                                        : {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                                        }
                                }
                            >
                                {isCurrentPlan && (
                                    <Box
                                        position="absolute"
                                        top={{ base: '-10px', md: '-12px' }}
                                        right={{ base: '10px', md: '20px' }}
                                        bg="brand.primary"
                                        color="white"
                                        px={{ base: 2, md: 3 }}
                                        py={1}
                                        borderRadius="full"
                                        fontSize={{ base: '10px', md: '12px' }}
                                        fontWeight={600}
                                        zIndex={1}
                                        whiteSpace="nowrap"
                                    >
                                        Current Plan
                                    </Box>
                                )}
                                {isNotSelectable && (
                                    <Box
                                        position="absolute"
                                        top={{ base: '-10px', md: '-12px' }}
                                        right={{ base: '10px', md: '20px' }}
                                        bg="gray.500"
                                        color="white"
                                        px={{ base: 2, md: 3 }}
                                        py={1}
                                        borderRadius="full"
                                        fontSize={{ base: '10px', md: '12px' }}
                                        fontWeight={600}
                                        zIndex={1}
                                        whiteSpace="nowrap"
                                    >
                                        Not Selectable
                                    </Box>
                                )}
                                <Flex
                                    flexDir="column"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    h="100%"
                                    gap={{ base: 3, md: 4 }}
                                >
                                    <Box w="100%" textAlign="center">
                                        <Heading
                                            mb={2}
                                            fontSize={{ base: '18px', md: '20px' }}
                                            fontWeight={700}
                                            color={isNotSelectable ? 'gray.400' : 'brand.primary'}
                                        >
                                            {plan?.name}
                                        </Heading>
                                        <Box mb={{ base: 3, md: 4 }}>
                                            <Text
                                                fontSize={{ base: '28px', md: '32px' }}
                                                fontWeight={700}
                                                color={isNotSelectable ? 'gray.400' : 'brand.primary'}
                                            >
                                                ${price.toFixed(2)}
                                            </Text>
                                            <Text fontSize={{ base: '12px', md: '14px' }} color="gray.600">
                                                /{plan?.billing_cycle || 'month'}
                                            </Text>
                                        </Box>
                                        <Text
                                            fontSize={{ base: '12px', md: '14px' }}
                                            color="gray.600"
                                            textAlign="center"
                                            mb={{ base: 3, md: 4 }}
                                            minH={{ base: '35px', md: '40px' }}
                                        >
                                            {plan?.description}
                                        </Text>
                                        {isNotSelectable && (
                                            <Text
                                                fontSize="12px"
                                                color="gray.500"
                                                fontStyle="italic"
                                                mt={2}
                                            >
                                                {isFreePlan ? 'Free plan cannot be selected' : 'Lower tier plan cannot be selected'}
                                            </Text>
                                        )}
                                    </Box>

                                    {/* Features List */}
                                    <Box
                                        w="100%"
                                        borderTop="1px solid"
                                        borderBottom="1px solid"
                                        borderColor="gray.200"
                                        py={{ base: 2, md: 3 }}
                                        maxH={{ base: '250px', md: '300px' }}
                                        overflowY="auto"
                                    >
                                        {(() => {
                                            const planFeaturesData = getPlanFeaturesForCard(plan?.name);

                                            if (planFeaturesData.isPro) {
                                                // Pro plan: Show message about Starter features + Pro unique features
                                                return (
                                                    <VStack spacing={3} align="stretch">
                                                        <Box
                                                            bg="rgba(19, 74, 112, 0.05)"
                                                            borderRadius="6px"
                                                            p={{ base: 1.5, md: 2 }}
                                                            border="1px solid"
                                                            borderColor="brand.primary"
                                                        >
                                                            <Text
                                                                fontSize={{ base: '11px', md: '12px' }}
                                                                fontWeight={600}
                                                                color="brand.primary"
                                                            >
                                                                ✓ Includes all Starter plan features
                                                            </Text>
                                                        </Box>

                                                        <Box>
                                                            <Text
                                                                fontSize={{ base: '11px', md: '12px' }}
                                                                fontWeight={600}
                                                                color="brand.primary"
                                                                mb={2}
                                                            >
                                                                Plus Pro exclusive:
                                                            </Text>
                                                            <VStack spacing={1.5} align="stretch" pl={1}>
                                                                {planFeaturesData.features.slice(0, 3).map((feature, index) => (
                                                                    <Flex
                                                                        key={index}
                                                                        alignItems="center"
                                                                        gap={2}
                                                                    >
                                                                        <Icon
                                                                            as={feature.isIncluded ? CheckIcon : CloseIcon}
                                                                            color={feature.isIncluded ? 'green.500' : 'gray.400'}
                                                                            boxSize={{ base: 3.5, md: 4 }}
                                                                            flexShrink={0}
                                                                        />
                                                                        <Text
                                                                            fontSize={{ base: '11px', md: '12px' }}
                                                                            fontWeight={400}
                                                                            color="gray.700"
                                                                            flex={1}
                                                                        >
                                                                            {feature.name}
                                                                        </Text>
                                                                    </Flex>
                                                                ))}
                                                            </VStack>
                                                        </Box>
                                                    </VStack>
                                                );
                                            } else if (planFeaturesData.isStarter) {
                                                // Starter plan: Show message about Free features + Starter unique features
                                                return (
                                                    <VStack spacing={3} align="stretch">
                                                        <Box
                                                            bg="rgba(19, 74, 112, 0.05)"
                                                            borderRadius="6px"
                                                            p={{ base: 1.5, md: 2 }}
                                                            border="1px solid"
                                                            borderColor="brand.primary"
                                                        >
                                                            <Text
                                                                fontSize={{ base: '11px', md: '12px' }}
                                                                fontWeight={600}
                                                                color="brand.primary"
                                                            >
                                                                ✓ Includes all Free plan features
                                                            </Text>
                                                        </Box>

                                                        <Box>
                                                            <Text
                                                                fontSize={{ base: '11px', md: '12px' }}
                                                                fontWeight={600}
                                                                color="brand.primary"
                                                                mb={2}
                                                            >
                                                                Plus Starter exclusive:
                                                            </Text>
                                                            <VStack spacing={1.5} align="stretch" pl={1}>
                                                                {planFeaturesData.features.slice(0, 5).map((feature, index) => (
                                                                    <Flex
                                                                        key={index}
                                                                        alignItems="center"
                                                                        gap={2}
                                                                    >
                                                                        <Icon
                                                                            as={feature.isIncluded ? CheckIcon : CloseIcon}
                                                                            color={feature.isIncluded ? 'green.500' : 'gray.400'}
                                                                            boxSize={{ base: 3.5, md: 4 }}
                                                                            flexShrink={0}
                                                                        />
                                                                        <Text
                                                                            fontSize={{ base: '11px', md: '12px' }}
                                                                            fontWeight={400}
                                                                            color="gray.700"
                                                                            flex={1}
                                                                        >
                                                                            {feature.name}
                                                                        </Text>
                                                                    </Flex>
                                                                ))}
                                                            </VStack>
                                                        </Box>
                                                    </VStack>
                                                );
                                            } else {
                                                return (
                                                    <VStack spacing={1.5} align="stretch">
                                                        {planFeaturesData.features.slice(0, 5).map((feature, index) => (
                                                            <Flex
                                                                key={index}
                                                                alignItems="center"
                                                                gap={2}
                                                            >
                                                                <Icon
                                                                    as={feature.isIncluded ? CheckIcon : CloseIcon}
                                                                    color={feature.isIncluded ? 'green.500' : 'gray.400'}
                                                                    boxSize={{ base: 3.5, md: 4 }}
                                                                    flexShrink={0}
                                                                />
                                                                <Text
                                                                    fontSize={{ base: '11px', md: '12px' }}
                                                                    fontWeight={400}
                                                                    color={feature.isIncluded ? 'gray.700' : 'gray.500'}
                                                                    flex={1}
                                                                >
                                                                    {feature.name}
                                                                </Text>
                                                            </Flex>
                                                        ))}
                                                        {planFeaturesData.features.length > 5 && (
                                                            <Text
                                                                fontSize={{ base: '10px', md: '11px' }}
                                                                color="gray.500"
                                                                fontStyle="italic"
                                                                mt={1}
                                                            >
                                                                +{planFeaturesData.features.length - 5} more features
                                                            </Text>
                                                        )}
                                                    </VStack>
                                                );
                                            }
                                        })()}
                                    </Box>

                                    <Box w="100%">
                                        {isCurrentPlan && !isFreePlan ? (
                                            !isCurrentPlanFree && onCancel ? (
                                                <>
                                                    {loggedInUserSubscription?.auto_renew ? (
                                                        <Button
                                                            w="100%"
                                                            bg="white"
                                                            color="red.500"
                                                            borderWidth="2px"
                                                            borderColor="red.300"
                                                            borderRadius="8px"
                                                            py={{ base: 5, md: 6 }}
                                                            fontSize={{ base: '14px', md: '16px' }}
                                                            fontWeight={600}
                                                            _hover={{
                                                                bg: 'red.50',
                                                                borderColor: 'red.400',
                                                                color: 'red.600',
                                                                transform: isDesktop ? 'translateY(-2px)' : 'none',
                                                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)',
                                                            }}
                                                            _active={{
                                                                bg: 'red.100',
                                                                borderColor: 'red.500',
                                                            }}
                                                            onClick={onCancel}
                                                            isLoading={isCancelling}
                                                            transition="all 0.2s ease"
                                                        >
                                                            Cancel Subscription
                                                        </Button>
                                                    ) : (
                                                        onResume && (
                                                            <Button
                                                                w="100%"
                                                                bg="brand.primary"
                                                                color="white"
                                                                borderRadius="8px"
                                                                py={{ base: 5, md: 6 }}
                                                                fontSize={{ base: '14px', md: '16px' }}
                                                                fontWeight={600}
                                                                _hover={{
                                                                    bg: 'brand.dark',
                                                                    transform: isDesktop ? 'translateY(-2px)' : 'none',
                                                                    boxShadow: '0 4px 12px rgba(19, 74, 112, 0.15)',
                                                                }}
                                                                _active={{
                                                                    bg: 'brand.secondary',
                                                                }}
                                                                onClick={onResume}
                                                                isLoading={isResuming}
                                                                transition="all 0.2s ease"
                                                            >
                                                                Resume Subscription
                                                            </Button>
                                                        )
                                                    )}

                                                </>

                                            ) : null
                                        ) : (
                                            <>
                                                {isNotSelectable ? (
                                                    <BrandButton
                                                        w="100%"
                                                        text={isFreePlan ? "View All Features" : "Upgrade"}
                                                        colorScheme="blue"
                                                        isDisabled={true}
                                                        opacity={0.5}
                                                        cursor="not-allowed"
                                                    />
                                                ) : (
                                                    <>
                                                        {isFreePlan ? (
                                                            <BrandButton
                                                                w="100%"
                                                                text="View All Features"
                                                                colorScheme="blue"
                                                                onClick={() => onUpgrade && onUpgrade(plan)}
                                                            />
                                                        ) : (
                                                            <BrandButton
                                                                w="100%"
                                                                text="Upgrade"
                                                                colorScheme="blue"
                                                                onClick={() => onUpgrade && onUpgrade(plan)}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </Flex>
                            </Box>
                        );
                    })
                ) : (
                    <Box textAlign="center" py={8}>
                        <Text color="gray.500">No subscription plans available</Text>
                    </Box>
                )}
            </Box>
        </>
    );
};


export const SelectedPlan = ({
    selectedPlan,
    selectedPackage,
    isDesktop,
    onSelectPackage,
    onCheckout,
    isLoading,
    onBack,
    userType = 'founder',
}) => {
    const price = parseFloat(selectedPlan?.price || 0);
    const monthlyPrice = price;
    const isFreePlan = price === 0;
    const isInvestor = userType?.toLowerCase() === 'investor';

    // Get features based on plan name
    const getPlanFeatures = (planName) => {
        const planNameLower = planName?.toLowerCase() || '';

        if (isInvestor) {
            // Investor plans
            if (planNameLower.includes('starter')) {
                return {
                    isPro: false,
                    isStarter: true,
                    features: investorStarterUniqueFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            } else if (planNameLower.includes('pro')) {
                return {
                    isPro: true,
                    isStarter: false,
                    features: investorProUniqueFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            } else {
                return {
                    isPro: false,
                    isStarter: false,
                    features: investorFreeFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            }
        } else {
            // Founder plans (default)
            if (planNameLower.includes('starter')) {
                return {
                    isPro: false,
                    isStarter: true,
                    features: founderStarterUniqueFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            } else if (planNameLower.includes('pro')) {
                return {
                    isPro: true,
                    isStarter: false,
                    features: founderProUniqueFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            } else {
                return {
                    isPro: false,
                    isStarter: false,
                    features: founderFreeFeatures.map(f => ({ ...f, isIncluded: f.value !== 'No' && !f.value.includes('(Coming Soon)') })),
                };
            }
        }
    };

    const planFeaturesData = getPlanFeatures(selectedPlan?.name);

    return (
        <Box display="flex" justifyContent="center" py={{ base: 4, md: 6 }}>
            <Box
                display="flex"
                flexDir={isDesktop ? 'row' : 'column'}
                justifyContent="center"
                alignItems="center"
                w="100%"
                maxW="600px"
            >
                {selectedPlan && typeof selectedPlan === 'object' && Object.keys(selectedPlan).length !== 0 ? (
                    <Box
                        p={{ base: 4, md: 6 }}
                        borderRadius="15px"
                        border="2px"
                        borderColor="brand.primary"
                        bg="white"
                        boxShadow="0 4px 12px rgba(19, 74, 112, 0.15)"
                        w="100%"
                    >
                        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                            <Flex
                                alignItems="center"
                                cursor="pointer"
                                onClick={onBack}
                                mb={2}
                                w="fit-content"
                                role="group"
                            >
                                <ArrowBackIcon
                                    fontSize="20px"
                                    color="gray.600"
                                    mr={2}
                                    transition="color 0.2s ease"
                                    _groupHover={{ color: 'brand.primary' }}
                                />
                                <Text
                                    fontSize="16px"
                                    fontWeight={600}
                                    color="gray.600"
                                    transition="color 0.2s ease"
                                    _groupHover={{ color: 'brand.primary' }}
                                >
                                    Back
                                </Text>
                            </Flex>
                            <Box textAlign="center">
                                <Heading mb={2} fontSize={{ base: '20px', md: '24px' }} fontWeight={700} color="brand.primary">
                                    {selectedPlan?.name}
                                </Heading>
                                <Text fontSize={{ base: '14px', md: '16px' }} color="gray.600" mb={4}>
                                    {selectedPlan?.description}
                                </Text>
                            </Box>

                            {/* Features List */}
                            <Box
                                w="100%"
                                borderTop="1px solid"
                                borderBottom="1px solid"
                                borderColor="gray.200"
                                py={{ base: 3, md: 4 }}
                            >
                                <Text
                                    fontSize={{ base: '16px', md: '18px' }}
                                    fontWeight={700}
                                    color="brand.primary"
                                    mb={{ base: 3, md: 4 }}
                                >
                                    Plan Features
                                </Text>

                                {planFeaturesData.isPro ? (
                                    // Pro plan: Show message about Starter features + Pro unique features
                                    <VStack spacing={4} align="stretch">
                                        <Box
                                            bg="rgba(19, 74, 112, 0.05)"
                                            borderRadius="8px"
                                            p={3}
                                            border="1px solid"
                                            borderColor="brand.primary"
                                        >
                                            <Text
                                                fontSize="14px"
                                                fontWeight={600}
                                                color="brand.primary"
                                            >
                                                ✓ Includes all Starter plan features
                                            </Text>
                                        </Box>

                                        <Box>
                                            <Text
                                                fontSize="14px"
                                                fontWeight={600}
                                                color="brand.primary"
                                                mb={3}
                                            >
                                                Plus Pro exclusive features:
                                            </Text>
                                            <VStack spacing={2} align="stretch" pl={2}>
                                                {planFeaturesData.features.map((feature, index) => (
                                                    <Flex
                                                        key={index}
                                                        alignItems="flex-start"
                                                        justifyContent="space-between"
                                                        gap={3}
                                                    >
                                                        <Flex alignItems="flex-start" gap={2} flex={1}>
                                                            <Icon
                                                                as={feature.isIncluded ? CheckIcon : CloseIcon}
                                                                color={feature.isIncluded ? 'green.500' : 'gray.400'}
                                                                boxSize={5}
                                                                mt={0.5}
                                                                flexShrink={0}
                                                            />
                                                            <Text
                                                                fontSize="14px"
                                                                fontWeight={500}
                                                                color="gray.700"
                                                                flex={1}
                                                            >
                                                                {feature.name}
                                                            </Text>
                                                        </Flex>
                                                        <Text
                                                            fontSize="14px"
                                                            color={feature.isIncluded ? 'gray.600' : 'gray.400'}
                                                            fontWeight={feature.isIncluded ? 500 : 400}
                                                            textAlign="right"
                                                            flexShrink={0}
                                                        >
                                                            {feature.value}
                                                        </Text>
                                                    </Flex>
                                                ))}
                                            </VStack>
                                        </Box>
                                    </VStack>
                                ) : planFeaturesData.isStarter ? (
                                    // Starter plan: Show message about Free features + Starter unique features
                                    <VStack spacing={4} align="stretch">
                                        <Box
                                            bg="rgba(19, 74, 112, 0.05)"
                                            borderRadius="8px"
                                            p={3}
                                            border="1px solid"
                                            borderColor="brand.primary"
                                        >
                                            <Text
                                                fontSize="14px"
                                                fontWeight={600}
                                                color="brand.primary"
                                            >
                                                ✓ Includes all Free plan features
                                            </Text>
                                        </Box>

                                        <Box>
                                            <Text
                                                fontSize="14px"
                                                fontWeight={600}
                                                color="brand.primary"
                                                mb={3}
                                            >
                                                Plus Starter exclusive features:
                                            </Text>
                                            <VStack spacing={2} align="stretch" pl={2}>
                                                {planFeaturesData.features.map((feature, index) => (
                                                    <Flex
                                                        key={index}
                                                        alignItems="flex-start"
                                                        justifyContent="space-between"
                                                        gap={3}
                                                    >
                                                        <Flex alignItems="flex-start" gap={2} flex={1}>
                                                            <Icon
                                                                as={feature.isIncluded ? CheckIcon : CloseIcon}
                                                                color={feature.isIncluded ? 'green.500' : 'gray.400'}
                                                                boxSize={5}
                                                                mt={0.5}
                                                                flexShrink={0}
                                                            />
                                                            <Text
                                                                fontSize="14px"
                                                                fontWeight={500}
                                                                color="gray.700"
                                                                flex={1}
                                                            >
                                                                {feature.name}
                                                            </Text>
                                                        </Flex>
                                                        <Text
                                                            fontSize="14px"
                                                            color={feature.isIncluded ? 'gray.600' : 'gray.400'}
                                                            fontWeight={feature.isIncluded ? 500 : 400}
                                                            textAlign="right"
                                                            flexShrink={0}
                                                        >
                                                            {feature.value}
                                                        </Text>
                                                    </Flex>
                                                ))}
                                            </VStack>
                                        </Box>
                                    </VStack>
                                ) : (
                                    // Free plan: Show simple list
                                    <VStack spacing={3} align="stretch">
                                        {planFeaturesData.features.map((feature, index) => (
                                            <Flex
                                                key={index}
                                                alignItems="flex-start"
                                                justifyContent="space-between"
                                                gap={3}
                                            >
                                                <Flex alignItems="flex-start" gap={2} flex={1}>
                                                    <Icon
                                                        as={feature.isIncluded ? CheckIcon : CloseIcon}
                                                        color={feature.isIncluded ? 'green.500' : 'gray.400'}
                                                        boxSize={5}
                                                        mt={0.5}
                                                        flexShrink={0}
                                                    />
                                                    <Text
                                                        fontSize="14px"
                                                        fontWeight={500}
                                                        color="gray.700"
                                                        flex={1}
                                                    >
                                                        {feature.name}
                                                    </Text>
                                                </Flex>
                                                <Text
                                                    fontSize="14px"
                                                    color={feature.isIncluded ? 'gray.600' : 'gray.400'}
                                                    fontWeight={feature.isIncluded ? 500 : 400}
                                                    textAlign="right"
                                                    flexShrink={0}
                                                >
                                                    {feature.value}
                                                </Text>
                                            </Flex>
                                        ))}
                                    </VStack>
                                )}
                            </Box>

                            {!isFreePlan && (
                                <>
                                    <Box
                                        display="flex"
                                        flexDir="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        w="100%"
                                        gap={3}
                                    >
                                        <Flex
                                            cursor="pointer"
                                            border="2px"
                                            borderColor={
                                                selectedPackage === 'Monthly'
                                                    ? 'brand.primary'
                                                    : 'gray.200'
                                            }
                                            borderRadius="10px"
                                            w="100%"
                                            minH="80px"
                                            px={6}
                                            py={4}
                                            alignItems="center"
                                            justifyContent="space-between"
                                            bg={selectedPackage === 'Monthly' ? 'rgba(19, 74, 112, 0.05)' : 'white'}
                                            onClick={() => onSelectPackage('Monthly')}
                                            transition="all 0.2s ease"
                                        >
                                            <Box>
                                                <Text fontWeight={700} fontSize="18px" color="brand.primary">
                                                    Pay Monthly
                                                </Text>
                                                <Text fontSize="14px" color="gray.600">
                                                    Billed monthly
                                                </Text>
                                            </Box>
                                            <Box textAlign="right">
                                                <Text fontSize="20px" fontWeight={700} color="brand.primary">
                                                    ${monthlyPrice.toFixed(2)}
                                                </Text>
                                                <Text fontSize="12px" color="gray.500">
                                                    per month
                                                </Text>
                                            </Box>
                                        </Flex>
                                    </Box>

                                    <BrandButton
                                        w="100%"
                                        text="Checkout"
                                        colorScheme="blue"
                                        onClick={onCheckout}
                                        isDisabled={!selectedPackage}
                                        isLoading={isLoading}
                                    />
                                </>
                            )}
                        </VStack>
                    </Box>
                ) : (
                    <Box textAlign="center" py={8}>
                        <Text color="gray.500">No plan selected</Text>
                    </Box>
                )}
            </Box>
        </Box>
    );
};



export const ThankYouPage = ({ onCancel, planName }) => {
    const planText = planName ? `${planName}` : 'premium';

    return (
        <>
            <VStack py={4} px={4} spacing={4}>
                <Image src={logo} boxSize={{ base: "80px", md: "100px" }} objectFit="contain" />
                <Image src={rocket} boxSize={{ base: "120px", md: "150px" }} objectFit="contain" />
                <Text color="brand.primary" fontWeight={600} fontSize="18px" textAlign="center">
                    Payment Successful
                </Text>
                <Box maxW="400px" px={2}>
                    <Text textAlign="center" color="gray.600" fontSize="14px" lineHeight="1.6">
                        Thank you for your subscription! Your payment has been processed successfully.
                        You now have full access to all {planText} features. Welcome aboard!
                    </Text>
                </Box>
                <BrandButton
                    text="Close"
                    w="120px"
                    mt={2}
                    onClick={onCancel}
                />
            </VStack>
        </>
    );
};



export const PaymentFailedPage = ({ onCancel, planName, onRetry }) => {
    const planText = planName ? `${planName}` : 'subscription';

    return (
        <>
            <VStack py={4} px={4} spacing={4}>
                <Image src={logo} boxSize={{ base: "80px", md: "100px" }} objectFit="contain" />
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w={{ base: "120px", md: "150px" }}
                    h={{ base: "120px", md: "150px" }}
                    borderRadius="full"
                    bg="red.50"
                >
                    <Icon as={WarningTwoIcon} boxSize={{ base: "60px", md: "75px" }} color="red.500" />
                </Box>
                <Text color="red.500" fontWeight={600} fontSize="18px" textAlign="center">
                    Payment Failed
                </Text>
                <Box maxW="400px" px={2}>
                    <Text textAlign="center" color="gray.600" fontSize="14px" lineHeight="1.6">
                        We're sorry, but your payment for the {planText} could not be processed.
                        Please check your payment details and try again, or contact support if the problem persists.
                    </Text>
                </Box>
                <Flex gap={3} mt={2}>
                    {onRetry && (
                        <BrandButton
                            text="Try Again"
                            w="120px"
                            onClick={onRetry}
                        />
                    )}
                    <BrandButton
                        text="Close"
                        w="120px"
                        onClick={onCancel}
                    />
                </Flex>
            </VStack>
        </>
    );
};

